import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, Form, Input, Button, Typography, Result, Space, message } from 'antd';
import { MailOutlined, PhoneOutlined, EnvironmentOutlined, SendOutlined } from '@ant-design/icons';
import { scenarioSiteAPI } from '../../../services/api';
import { getContentWrapperStyle } from '../templateStyles';

const { Title, Text, Paragraph } = Typography;

export default function SectionInquiryForm({ slug, section, siteInfo }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const contact = section.content_data || {};

  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';
  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 8;
  const contentFont = getContentWrapperStyle(template, brandColor);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      await scenarioSiteAPI.submitInquiry(slug, { ...values, inquiry_type: 'inquiry' });
      setSubmitted(true);
      message.success(t('sections.inquiry.submitted'));
    } catch (e) {
      message.error(e.message || t('sections.inquiry.submitFailed'));
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px' }}>
        <Result status="success" title={t('sections.inquiry.thankYou')} subTitle={t('sections.inquiry.willContact')} extra={<Button onClick={() => { setSubmitted(false); form.resetFields(); }}>{t('sections.inquiry.contactAgain')}</Button>} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px', ...contentFont }} id="inquiry-form">
      <Title level={2} style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 8,
        fontWeight: isMinimal ? 300 : 600,
        letterSpacing: isMinimal ? 1 : 0,
        borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
        paddingBottom: isMinimal ? 8 : 0,
        display: isMinimal ? 'inline-block' : 'block',
      }}>
        {section.title || t('sections.inquiry.title')}
      </Title>
      {section.subtitle && <Paragraph style={{ textAlign: isMinimal ? 'left' : 'center', color: '#666', marginBottom: 32 }}>{section.subtitle}</Paragraph>}
      <Row gutter={[32, 24]}>
        <Col xs={24} md={14}>
          <Card style={{ borderRadius: cardRadius, border: isMinimal ? '1px solid #e8e8e8' : undefined }}>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="contact_name" label={t('sections.inquiry.nameLabel')} rules={[{ required: true, message: t('sections.inquiry.nameRequired') }]}>
                    <Input placeholder={t('sections.inquiry.namePlaceholder')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="company_name" label={t('sections.inquiry.companyLabel')}>
                    <Input placeholder={t('sections.inquiry.companyPlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="email" label={t('sections.inquiry.emailLabel')} rules={[{ required: true, type: 'email', message: t('sections.inquiry.emailRequired') }]}>
                    <Input placeholder="your@email.com" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="phone" label={t('sections.inquiry.phoneLabel')}>
                    <Input placeholder={t('sections.inquiry.phonePlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="message" label={t('sections.inquiry.messageLabel')} rules={[{ required: true, message: t('sections.inquiry.messageRequired') }]}>
                <Input.TextArea rows={4} placeholder={t('sections.inquiry.messagePlaceholder')} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} icon={<SendOutlined />}
                style={{
                  background: brandColor,
                  borderColor: brandColor,
                  borderRadius: isMinimal ? 0 : isClassic ? 4 : undefined,
                }}>
                {t('sections.inquiry.submitButton')}
              </Button>
            </Form>
          </Card>
        </Col>
        <Col xs={24} md={10}>
          <Card style={{ height: '100%', borderRadius: cardRadius, border: isMinimal ? '1px solid #e8e8e8' : undefined }}>
            <Title level={4} style={{ marginBottom: 16 }}>{t('sections.inquiry.contactInfo')}</Title>
            <Space direction="vertical" size={12}>
              {contact.company_name && <Text strong style={{ fontSize: 16 }}>{contact.company_name}</Text>}
              {contact.contact_email && <Text><MailOutlined style={{ marginRight: 8, color: brandColor }} /><a href={`mailto:${contact.contact_email}`}>{contact.contact_email}</a></Text>}
              {contact.contact_phone && <Text><PhoneOutlined style={{ marginRight: 8, color: brandColor }} /><a href={`tel:${contact.contact_phone}`}>{contact.contact_phone}</a></Text>}
              {contact.address && <Text><EnvironmentOutlined style={{ marginRight: 8, color: brandColor }} />{contact.address}</Text>}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
